import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  createFilterOptions,
} from "@mui/material";

import {
  Article,
  ArrowBack,
  ArrowForward,
  Business,
  Close,
  Download,
  Event,
  ExpandMore,
  LocationOn,
  Person,
  Refresh,
  Search,
  Visibility,
  Work,
} from "@mui/icons-material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import {
  Candidate,
  getCandidateCV,
  getCandidatesByJobId,
  getJobById,
  getJobsList,
  JobDetail,
  JobLS,
  previewCandidateCV,
  updateCandidateStatus,
  downloadSelectedCVs,
} from "../Services/jobCandidateService";

type Order = "asc" | "desc";

type OrderBy =
  | "name"
  | "currentDesignation"
  | "currentOrganization"
  | "totalExperience"
  | "status"
  | "totalScore"
  | "submitted";

interface EvaluationCriterion {
  name: string;
  weight: number;
  description: string;
}

interface LoadingState {
  jobs: boolean;
  jobDetail: boolean;
  candidates: boolean;
  cv: boolean;
}

interface ErrorState {
  jobs: string;
  jobDetail: string;
  candidates: string;
  cv: string;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];

const jobFilter = createFilterOptions<JobLS>({
  stringify: (option) =>
    `${option.jobTitle || ""} ${option.status || ""} ${option.jobId || ""}`,
  trim: true,
});

interface CandidateTrackingPageProps {
  initialJobId?: string;
  initialJobStatus?: string;
}

const normalizeJobStatus = (status?: string) => {
  const value = (status || "").toLowerCase().replace(/\s+/g, "");
  if (value === "open") return "Open";
  if (value === "inreview" || value === "processing") return "In Review";
  if (value === "closed" || value === "reviewed") return "Reviewed";
  return "";
};

const CandidateTrackingPage: React.FC<CandidateTrackingPageProps> = ({
  initialJobId,
  initialJobStatus,
}) => {
  const [jobsList, setJobsList] = useState<JobLS[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [selectedJobInput, setSelectedJobInput] = useState("");

  const [jobDetail, setJobDetail] = useState<JobDetail | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [evaluationCriteria, setEvaluationCriteria] = useState<
    EvaluationCriterion[]
  >([]);

  const [jobStatusFilter, setJobStatusFilter] = useState("");
  const [candidateStatusFilter, setCandidateStatusFilter] = useState("All");

  const [search, setSearch] = useState("");
  const [serverSearch, setServerSearch] = useState("");

  const [expandedCandidateId, setExpandedCandidateId] = useState<string | null>(
    null,
  );
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);

  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<OrderBy>("totalScore");

  const [downloadingCandidateId, setDownloadingCandidateId] = useState<
    string | null
  >(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewCandidateIndex, setPreviewCandidateIndex] = useState(0);
  const [previewCandidateName, setPreviewCandidateName] = useState("");

  const [paginationData, setPaginationData] = useState({
    totalCount: 0,
    pageNumber: 1,
    pageSize: 50,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  });

  const [loading, setLoading] = useState<LoadingState>({
    jobs: false,
    jobDetail: false,
    candidates: false,
    cv: false,
  });

  const [error, setError] = useState<ErrorState>({
    jobs: "",
    jobDetail: "",
    candidates: "",
    cv: "",
  });

  const setLoadingState = (key: keyof LoadingState, value: boolean) => {
    setLoading((prev) => ({ ...prev, [key]: value }));
  };

  const setErrorState = (key: keyof ErrorState, value: string) => {
    setError((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (!initialJobId) return;
    const status = normalizeJobStatus(initialJobStatus);
    setJobStatusFilter(status);
    setSelectedJobId(initialJobId);
    if (status) fetchJobs(status);
  }, [initialJobId, initialJobStatus]);

  const parseEvaluationCriteria = (value?: string): EvaluationCriterion[] => {
    try {
      if (!value) return [];

      const parsed = JSON.parse(value);
      const normalized =
        typeof parsed === "string" ? JSON.parse(parsed) : parsed;

      return normalized?.criteria || [];
    } catch {
      return [];
    }
  };

  const parseCriteriaScores = (
    value?: string,
  ): Record<
    string,
    {
      score?: number;
      Score?: number;
      matchPercentage?: number;
      MatchPercentage?: number;
    }
  > => {
    try {
      if (!value) return {};

      const parsed = JSON.parse(value);
      const normalized =
        typeof parsed === "string" ? JSON.parse(parsed) : parsed;

      return normalized || {};
    } catch {
      return {};
    }
  };

  /**
   * Fix for answers showing as JSON array in first question.
   *
   * Handles all possible backend shapes:
   * 1. ["answer 1", "answer 2"]
   * 2. ["[\"answer 1\", \"answer 2\"]"]
   * 3. "[\"answer 1\", \"answer 2\"]"
   * 4. "single answer"
   */
  const normalizeAnswers = (answers?: string[] | string): string[] => {
    if (!answers) return [];

    if (Array.isArray(answers)) {
      if (answers.length === 1 && typeof answers[0] === "string") {
        const first = answers[0].trim();

        if (first.startsWith("[") && first.endsWith("]")) {
          try {
            const parsed = JSON.parse(first);
            return Array.isArray(parsed)
              ? parsed.map((x) => String(x ?? ""))
              : answers;
          } catch {
            return answers;
          }
        }
      }

      return answers.map((x) => String(x ?? ""));
    }

    if (typeof answers === "string") {
      const text = answers.trim();

      if (text.startsWith("[") && text.endsWith("]")) {
        try {
          const parsed = JSON.parse(text);
          return Array.isArray(parsed)
            ? parsed.map((x) => String(x ?? ""))
            : [answers];
        } catch {
          return [answers];
        }
      }

      return [answers];
    }

    return [];
  };

  const getCandidateAnswer = (candidate: Candidate, index: number) => {
    const normalizedAnswers = normalizeAnswers(candidate.answers);
    return normalizedAnswers[index] || "No answer provided.";
  };

  const getScoreValue = (
    candidate: Candidate,
    criterionName: string,
  ): number => {
    const scores = parseCriteriaScores(candidate.criteriaScore);

    return scores[criterionName]?.score ?? scores[criterionName]?.Score ?? 0;
  };

  const getApplicationStageColor = (
    status?: string,
  ):
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning" => {
    const normalized = (status || "").toLowerCase();

    if (normalized === "shortlisted") return "primary";
    if (normalized === "finalized") return "success";
    if (normalized === "rejected") return "error";
    if (normalized === "applied") return "info";
    if (normalized === "ranked") return "success";
    if (normalized === "ranking failed") return "error";

    return "default";
  };

  const getRankingChip = (candidate: Candidate) => {
    if (!candidate.isRanked) {
      return <Chip size="small" label="Pending" variant="outlined" />;
    }

    if ((candidate.status || "").toLowerCase() === "ranking failed") {
      return (
        <Chip size="small" label="Failed" color="error" variant="outlined" />
      );
    }

    const score = candidate.totalScore || 0;

    const color =
      score >= 80
        ? "success"
        : score >= 60
          ? "info"
          : score >= 40
            ? "warning"
            : "error";

    return (
      <Chip
        size="small"
        label={`${score}/100`}
        color={color}
        sx={{ fontWeight: 900 }}
      />
    );
  };

  const formatWeight = (weight?: number) =>
    `${Math.round((weight || 0) * 100)}%`;

  const getInitials = (name?: string) => {
    if (!name) return "?";

    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((x) => x[0]?.toUpperCase())
      .join("");
  };

  const fetchJobs = async (status: string) => {
    if (!status) return;

    try {
      setLoadingState("jobs", true);
      setErrorState("jobs", "");

      const data = await getJobsList(status);
      setJobsList(data || []);
    } catch (err) {
      setErrorState(
        "jobs",
        err instanceof Error ? err.message : "Failed to fetch jobs.",
      );
      setJobsList([]);
    } finally {
      setLoadingState("jobs", false);
    }
  };

  const fetchJobDetail = async (jobId: string) => {
    try {
      setLoadingState("jobDetail", true);
      setErrorState("jobDetail", "");

      const data = await getJobById(jobId);

      setJobDetail(data);
      setEvaluationCriteria(parseEvaluationCriteria(data.evaluationCriteria));
    } catch (err) {
      setErrorState(
        "jobDetail",
        err instanceof Error ? err.message : "Failed to fetch job detail.",
      );
      setJobDetail(null);
      setEvaluationCriteria([]);
    } finally {
      setLoadingState("jobDetail", false);
    }
  };

  const fetchCandidates = async () => {
    if (!jobDetail?.jobId) return;

    try {
      setLoadingState("candidates", true);
      setErrorState("candidates", "");

      const result = await getCandidatesByJobId(
        jobDetail.jobId,
        paginationData.pageNumber,
        paginationData.pageSize,
        candidateStatusFilter === "All" ? undefined : candidateStatusFilter,
        serverSearch || undefined,
      );

      setCandidates(result.items || []);
      setPaginationData({
        totalCount: result.totalCount || 0,
        pageNumber: result.pageNumber || 1,
        pageSize: result.pageSize || 50,
        totalPages: result.totalPages || 0,
        hasPreviousPage: result.hasPreviousPage || false,
        hasNextPage: result.hasNextPage || false,
      });
    } catch (err) {
      setErrorState(
        "candidates",
        err instanceof Error ? err.message : "Failed to fetch candidates.",
      );
      setCandidates([]);
    } finally {
      setLoadingState("candidates", false);
    }
  };

  useEffect(() => {
    if (!selectedJobId) {
      setJobDetail(null);
      setCandidates([]);
      setEvaluationCriteria([]);
      return;
    }

    fetchJobDetail(selectedJobId);
  }, [selectedJobId]);

  useEffect(() => {
    if (jobDetail?.jobId) {
      fetchCandidates();
    }
  }, [
    jobDetail?.jobId,
    paginationData.pageNumber,
    paginationData.pageSize,
    candidateStatusFilter,
    serverSearch,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPaginationData((prev) => ({ ...prev, pageNumber: 1 }));
      setServerSearch(search.trim());
    }, 450);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const compareValues = (a: Candidate, b: Candidate, field: OrderBy) => {
    const aValue = a[field as keyof Candidate];
    const bValue = b[field as keyof Candidate];

    if (field === "totalScore") {
      return (Number(bValue) || 0) - (Number(aValue) || 0);
    }

    return String(bValue || "").localeCompare(String(aValue || ""));
  };

  const sortedCandidates = useMemo(() => {
    return [...candidates].sort((a, b) => {
      const result = compareValues(a, b, orderBy);
      return order === "desc" ? result : -result;
    });
  }, [candidates, order, orderBy]);

  const stats = useMemo(() => {
    const total = paginationData.totalCount || candidates.length;
    const ranked = candidates.filter((x) => x.isRanked).length;
    const pendingRanking = candidates.filter((x) => !x.isRanked).length;

    const shortlisted = candidates.filter(
      (x) => x.status?.toLowerCase() === "shortlisted",
    ).length;

    const rejected = candidates.filter(
      (x) => x.status?.toLowerCase() === "rejected",
    ).length;

    const averageScore =
      candidates.length > 0
        ? Math.round(
            candidates.reduce((sum, item) => sum + (item.totalScore || 0), 0) /
              candidates.length,
          )
        : 0;

    return {
      total,
      ranked,
      pendingRanking,
      shortlisted,
      rejected,
      averageScore,
    };
  }, [candidates, paginationData.totalCount]);

  const handleSort = (field: OrderBy) => {
    const isAsc = orderBy === field && order === "asc";

    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(field);
  };

  const resetCandidateFilters = () => {
    setSearch("");
    setServerSearch("");
    setCandidateStatusFilter("All");
    setExpandedCandidateId(null);
    setSelectedCandidates([]);
    setPaginationData((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const handleJobStatusChange = (event: SelectChangeEvent) => {
    const status = event.target.value;

    setJobStatusFilter(status);
    setSelectedJobId(null);
    setSelectedJobInput("");
    setJobDetail(null);
    setCandidates([]);
    setJobsList([]);
    resetCandidateFilters();

    if (status) {
      fetchJobs(status);
    }
  };

  const handleJobChange = (job: JobLS | null) => {
    setSelectedJobId(job?.jobId || null);
    resetCandidateFilters();
  };

  const handleCandidateStatusChange = async (
    candidateId: string,
    status: string,
  ) => {
    try {
      await updateCandidateStatus(candidateId, status);

      setCandidates((prev) =>
        prev.map((candidate) =>
          candidate.id === candidateId
            ? {
                ...candidate,
                status,
              }
            : candidate,
        ),
      );
    } catch (err) {
      setErrorState(
        "candidates",
        err instanceof Error
          ? err.message
          : "Failed to update candidate status.",
      );
    }
  };

  const handleCandidateSelection = (candidateId: string, checked: boolean) => {
    if (checked) {
      if (selectedCandidates.length >= 20) {
        alert("Maximum 20 candidates can be selected.");
        return;
      }

      setSelectedCandidates((prev) => [...prev, candidateId]);
      return;
    }

    setSelectedCandidates((prev) => prev.filter((id) => id !== candidateId));
  };

  const handleSelectAllVisible = (checked: boolean) => {
    if (!checked) {
      setSelectedCandidates([]);
      return;
    }

    setSelectedCandidates(
      sortedCandidates.slice(0, 20).map((candidate) => candidate.id),
    );
  };

  const handleDownloadCV = async (candidate: Candidate) => {
    try {
      setDownloadingCandidateId(candidate.id);
      setLoadingState("cv", true);
      setErrorState("cv", "");

      const response = await getCandidateCV(candidate.id);
      const blob = response.data;

      const contentDisposition = response.headers["content-disposition"];
      let fileName = `CV_${candidate.name || candidate.id}`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match?.[1]) fileName = match[1];
      }

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      setErrorState(
        "cv",
        err instanceof Error ? err.message : "Failed to download CV.",
      );
    } finally {
      setDownloadingCandidateId(null);
      setLoadingState("cv", false);
    }
  };

  const handleBulkDownload = async () => {
    if (!jobDetail || selectedCandidates.length === 0) return;

    try {
      setLoadingState("cv", true);
      setErrorState("cv", "");

      const response = await downloadSelectedCVs(
        jobDetail.jobId,
        selectedCandidates,
      );
      const blob = response.data;

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "Selected_CVs.zip";

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      setErrorState(
        "cv",
        err instanceof Error ? err.message : "Failed to download selected CVs.",
      );
    } finally {
      setLoadingState("cv", false);
    }
  };

  const handlePreviewCV = async (candidate: Candidate, index: number) => {
    try {
      setPreviewLoading(true);
      setPreviewCandidateIndex(index);
      setPreviewCandidateName(candidate.name || "Candidate");

      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
        setPreviewUrl("");
      }

      const response = await previewCandidateCV(candidate.id);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);

      setPreviewUrl(url);
      setPreviewOpen(true);
    } catch (err) {
      setErrorState(
        "cv",
        err instanceof Error ? err.message : "Failed to preview CV.",
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const goToPreviewCandidate = (direction: "next" | "previous") => {
    const nextIndex =
      direction === "next"
        ? previewCandidateIndex + 1
        : previewCandidateIndex - 1;

    if (nextIndex < 0 || nextIndex >= sortedCandidates.length) return;

    handlePreviewCV(sortedCandidates[nextIndex], nextIndex);
  };

  const renderMetricCard = (
    label: string,
    value: string | number,
    helper?: string,
  ) => (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 3,
        height: "100%",
        bgcolor: "background.paper",
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>

      <Typography variant="h5" fontWeight={900} sx={{ mt: 0.5 }}>
        {value}
      </Typography>

      {helper && (
        <Typography variant="caption" color="text.secondary">
          {helper}
        </Typography>
      )}
    </Paper>
  );

  const renderJobSummary = () => {
    if (!jobDetail) return null;

    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            <Box>
              <Typography variant="h5" fontWeight={900}>
                {jobDetail.jobTitle || "Untitled Job"}
              </Typography>

              <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 1 }}>
                <Stack direction="row" alignItems="center" spacing={0.7}>
                  <LocationOn fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {jobDetail.location || "No location"}
                  </Typography>
                </Stack>

                {jobDetail.deadline && (
                  <Stack direction="row" alignItems="center" spacing={0.7}>
                    <Event fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Deadline:{" "}
                      {new Date(jobDetail.deadline).toLocaleDateString()}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>

            <Chip
              label={`Job: ${jobDetail.status || "No Status"}`}
              variant="outlined"
              color="primary"
              sx={{ fontWeight: 800 }}
            />
          </Stack>

          {jobDetail.AdditionalInformation && (
            <Typography variant="body2" color="text.secondary">
              {jobDetail.AdditionalInformation}
            </Typography>
          )}

          {evaluationCriteria.length > 0 && (
            <>
              <Divider />

              <Box>
                <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                  Evaluation Criteria
                </Typography>

                <Grid container spacing={1.5}>
                  {evaluationCriteria.map((criterion, index) => (
                    <Grid
                      item
                      xs={12}
                      md={6}
                      key={`${criterion.name}-${index}`}
                    >
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1,
                          borderRadius: 2,
                          bgcolor: "background.default",
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          spacing={0.5}
                        >
                          <Typography variant="body2" fontWeight={700}>
                            {criterion.name}
                          </Typography>

                          <Chip
                            size="small"
                            label={formatWeight(criterion.weight)}
                          />
                        </Stack>

                        {criterion.description && (
                          <Typography variant="caption" color="text.secondary">
                            {criterion.description}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </>
          )}
        </Stack>
      </Paper>
    );
  };

  const renderCandidateDetails = (candidate: Candidate) => (
    <Box sx={{ p: 2.5, bgcolor: "background.default" }}>
      <Grid container spacing={3}>
        {/* Smaller first column */}
        <Grid item xs={12} md={3}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Work fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={900}>
              Professional Summary
            </Typography>
          </Stack>

          <Stack spacing={1.5}>
            <Typography variant="body2">
              <b>Highest Education:</b> {candidate.highestEducation || "-"}
            </Typography>

            <Typography variant="body2">
              <b>Current Organization:</b>{" "}
              {candidate.currentOrganization || "-"}
            </Typography>

            <Typography variant="body2">
              <b>Current Designation:</b> {candidate.currentDesignation || "-"}
            </Typography>

            <Typography variant="body2">
              <b>Total Experience:</b> {candidate.totalExperience || "-"}
            </Typography>

            <Typography variant="body2">
              <b>Relevant Experience:</b> {candidate.relevantExperience || "-"}
            </Typography>

            <Typography variant="body2">
              <b>Key Expertise:</b> {candidate.keyExpertise || "-"}
            </Typography>

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle2" fontWeight={900}>
              Compensation
            </Typography>

            <Typography variant="body2">
              <b>Current Salary:</b> {candidate.currentSalary || "-"}
            </Typography>

            <Typography variant="body2">
              <b>Benefits:</b> {candidate.benefits || "-"}
            </Typography>

            <Typography variant="body2">
              <b>Expected Salary:</b> {candidate.expectedSalary || "-"}
            </Typography>
          </Stack>
        </Grid>

        {/* Wider answers column */}
        <Grid item xs={12} md={5}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Article fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={900}>
              Screening Answers
            </Typography>
          </Stack>

          {jobDetail?.screeningQuestions?.length ? (
            <Stack spacing={1.5}>
              {jobDetail.screeningQuestions.map((question, index) => (
                <Box
                  key={`${candidate.id}-answer-${index}`}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: 0.5 }}
                  >
                    Q{index + 1}: {question}
                  </Typography>

                  <Typography variant="body2">
                    {getCandidateAnswer(candidate, index)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No screening questions available.
            </Typography>
          )}
        </Grid>

        {/* Ranking column */}
        <Grid item xs={12} md={4}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <SmartToyIcon fontSize="small" color="success" />
            <Typography variant="subtitle2" fontWeight={900}>
              AI Ranking
            </Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {candidate.rankingJustification ||
              "Ranking has not been generated yet."}
          </Typography>

          {evaluationCriteria.length > 0 ? (
            <Stack spacing={1.4}>
              {evaluationCriteria.map((criterion) => {
                const score = getScoreValue(candidate, criterion.name);

                return (
                  <Box key={`${candidate.id}-${criterion.name}`}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Typography variant="caption">
                        {criterion.name}
                      </Typography>
                      <Typography variant="caption" fontWeight={800}>
                        {score}
                      </Typography>
                    </Stack>

                    <LinearProgress
                      variant="determinate"
                      value={Math.min(score, 100)}
                      sx={{ height: 7, borderRadius: 99, mt: 0.5 }}
                    />
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Evaluation criteria are not available yet.
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          {/* <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 2 }}>
          {[
            { label: "Shortlist", status: "Shortlisted", color: "primary" },
            { label: "Finalize", status: "Finalized", color: "success" },
            { label: "Reject", status: "Rejected", color: "error" },
          ].map((item) => (
            <Button
              key={item.status}
              size="small"
              variant={candidate.status === item.status ? "contained" : "outlined"}
              color={item.color as "primary" | "success" | "error"}
              disabled={candidate.status === item.status}
              onClick={() => handleCandidateStatusChange(candidate.id, item.status)}
            >
              {item.label}
            </Button>
          ))}
        </Stack> */}
        </Grid>
      </Grid>
    </Box>
  );

  const renderCandidateRow = (candidate: Candidate, index: number) => {
    const expanded = expandedCandidateId === candidate.id;

    return (
      <React.Fragment key={candidate.id}>
        <TableRow hover>
          <TableCell padding="checkbox">
            <Checkbox
              checked={selectedCandidates.includes(candidate.id)}
              onChange={(event) =>
                handleCandidateSelection(candidate.id, event.target.checked)
              }
            />
          </TableCell>

          <TableCell sx={{ minWidth: 260 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box>
                <Typography variant="body2" fontWeight={900}>
                  {candidate.name || "Unknown"}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {candidate.email || "No email"}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {candidate.phone || "No phone"}
                </Typography>
              </Box>
            </Stack>
          </TableCell>

          <TableCell sx={{ minWidth: 260 }}>
            <Typography variant="body2" fontWeight={700}>
              {candidate.currentDesignation || "-"}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {candidate.currentOrganization || "-"}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {candidate.highestEducation || "-"}
            </Typography>
          </TableCell>

          <TableCell sx={{ minWidth: 190 }}>
            <Typography variant="body2">
              Total: <b>{candidate.totalExperience || "-"}</b>
            </Typography>

            <Typography variant="caption" color="text.secondary">
              Relevant: {candidate.relevantExperience || "-"}
            </Typography>
          </TableCell>

          <TableCell sx={{ minWidth: 230 }}>
            <Tooltip title={candidate.keyExpertise || ""}>
              <Typography
                variant="body2"
                sx={{
                  maxWidth: 220,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {candidate.keyExpertise || "-"}
              </Typography>
            </Tooltip>

            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Expected: {candidate.expectedSalary || "-"}
            </Typography>
          </TableCell>

          {/* <TableCell sx={{ minWidth: 150 }}>
            <Chip
              size="small"
              label={candidate.status || "Applied"}
              color={getApplicationStageColor(candidate.status)}
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
          </TableCell> */}

          <TableCell align="center" sx={{ minWidth: 130 }}>
            {getRankingChip(candidate)}
          </TableCell>

          <TableCell align="right" sx={{ minWidth: 160 }}>
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
              {candidate.cvFilePath && (
                <Tooltip title="Download CV">
                  <IconButton
                    size="small"
                    onClick={() => handleDownloadCV(candidate)}
                    disabled={
                      loading.cv && downloadingCandidateId === candidate.id
                    }
                  >
                    {loading.cv && downloadingCandidateId === candidate.id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Download fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="Preview CV">
                <IconButton
                  size="small"
                  onClick={() => handlePreviewCV(candidate, index)}
                >
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title={expanded ? "Hide details" : "View details"}>
                <IconButton
                  size="small"
                  onClick={() =>
                    setExpandedCandidateId(expanded ? null : candidate.id)
                  }
                >
                  <ExpandMore
                    fontSize="small"
                    sx={{
                      transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "0.2s",
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Stack>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell
            colSpan={8}
            sx={{ p: 0, borderBottom: expanded ? undefined : "none" }}
          >
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              {renderCandidateDetails(candidate)}
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  const renderTableHeader = () => {
    const headerCells: {
      key?: OrderBy;
      label: string;
      align?: "left" | "center" | "right";
    }[] = [
      { key: "name", label: "Candidate" },
      { key: "currentDesignation", label: "Professional Summary" },
      { key: "totalExperience", label: "Experience" },
      { label: "Expertise / Salary" },
      // { key: "status", label: "Application Stage" },
      { key: "totalScore", label: "AI Ranking", align: "center" },
      { label: "Actions", align: "center" },
    ];

    return (
      <TableHead>
        <TableRow sx={{ bgcolor: "background.default" }}>
          <TableCell padding="checkbox">
            <Checkbox
              checked={
                sortedCandidates.length > 0 &&
                sortedCandidates.every((candidate) =>
                  selectedCandidates.includes(candidate.id),
                )
              }
              indeterminate={
                selectedCandidates.length > 0 &&
                selectedCandidates.length < sortedCandidates.length
              }
              onChange={(event) => handleSelectAllVisible(event.target.checked)}
            />
          </TableCell>

          {headerCells.map((cell) => (
            <TableCell
              key={cell.label}
              align={cell.align || "left"}
              sx={{ fontWeight: 900, whiteSpace: "nowrap" }}
            >
              {cell.key ? (
                <TableSortLabel
                  active={orderBy === cell.key}
                  direction={orderBy === cell.key ? order : "asc"}
                  onClick={() => handleSort(cell.key!)}
                >
                  {cell.label}
                </TableSortLabel>
              ) : (
                cell.label
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  return (
    <Box>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight={700} letterSpacing="-0.03em">
            Candidates
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Select a job, review applications, and compare AI ranking results.
            {/* , and move candidates through the hiring process. */}
          </Typography>
        </Box>

        <Paper
          variant="outlined"
          sx={{ p: 2.5, borderRadius: 3, bgcolor: "background.paper" }}
        >
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Business color="primary" />
              <Box>
                <Typography variant="h6" fontWeight={900}>
                  Job Selection
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Filter by job status, then search and select a job title.
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={1}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Job Status
                  </Typography>

                  <Select
                    value={jobStatusFilter}
                    onChange={handleJobStatusChange}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Select status</em>
                    </MenuItem>
                    <MenuItem value="Open">Open</MenuItem>
                    <MenuItem value="In Review">In Review</MenuItem>
                    <MenuItem value="Reviewed">Reviewed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={9}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Search Job Title
                </Typography>

                <Autocomplete
                  options={jobsList}
                  filterOptions={jobFilter}
                  loading={loading.jobs}
                  disabled={!jobStatusFilter || loading.jobs}
                  value={
                    jobsList.find((job) => job.jobId === selectedJobId) || null
                  }
                  inputValue={selectedJobInput}
                  onInputChange={(_, value) => setSelectedJobInput(value)}
                  onChange={(_, value) => handleJobChange(value)}
                  getOptionLabel={(job) => job?.jobTitle || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.jobId === value.jobId
                  }
                  openOnFocus
                  clearOnBlur={false}
                  noOptionsText={
                    jobStatusFilter
                      ? "No matching jobs found"
                      : "Select job status first"
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.jobId}>
                      <Stack spacing={0.2}>
                        <Typography variant="body2" fontWeight={700}>
                          {option.jobTitle}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.status}
                        </Typography>
                      </Stack>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder={
                        jobStatusFilter
                          ? "Type job title to search..."
                          : "Select job status first"
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <Search
                                color={jobStatusFilter ? "primary" : "disabled"}
                              />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {loading.jobs ? (
                              <CircularProgress size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {error.jobs && <Alert severity="error">{error.jobs}</Alert>}

            {loading.jobDetail ? (
              <Box>
                <Skeleton variant="text" height={40} width="50%" />
                <Skeleton variant="text" height={22} width="75%" />
                <Skeleton variant="text" height={22} width="35%" />
              </Box>
            ) : error.jobDetail ? (
              <Alert severity="error">{error.jobDetail}</Alert>
            ) : jobDetail ? (
              renderJobSummary()
            ) : (
              <Alert severity="info">
                {jobStatusFilter
                  ? "Search and select a job to view candidate applications."
                  : "Start by selecting a job status."}
              </Alert>
            )}
          </Stack>
        </Paper>

        {jobDetail && (
          <Paper
            variant="outlined"
            sx={{ p: 2.5, borderRadius: 3, bgcolor: "background.paper" }}
          >
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={1.5}
              >
                <Box>
                  <Typography variant="h6" fontWeight={900}>
                    Applications
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Candidate applications for {jobDetail.jobTitle}
                  </Typography>
                </Box>

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchCandidates}
                  disabled={loading.candidates}
                >
                  Refresh
                </Button>
              </Stack>

              <Grid container spacing={1.5}>
                <Grid item xs={6} md={2}>
                  {renderMetricCard("Total", stats.total)}
                </Grid>
                <Grid item xs={6} md={2}>
                  {renderMetricCard("Ranked", stats.ranked)}
                </Grid>
                <Grid item xs={6} md={2}>
                  {renderMetricCard("Pending Ranking", stats.pendingRanking)}
                </Grid>
                {/* <Grid item xs={6} md={2}>
                  {renderMetricCard("Shortlisted", stats.shortlisted)}
                </Grid>
                <Grid item xs={6} md={2}>
                  {renderMetricCard("Rejected", stats.rejected)}
                </Grid> */}
                {/* <Grid item xs={6} md={2}>
                  {renderMetricCard("Avg. Score", `${stats.averageScore}/100`)}
                </Grid> */}
              </Grid>

              <Paper
                variant="outlined"
                sx={{ p: 2, borderRadius: 3, bgcolor: "background.default" }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "center" }}
                >
                  <TextField
                    size="small"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search name, email, phone, organization, designation, expertise"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ minWidth: { xs: "100%", md: 440 } }}
                  />

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    {/* <FormControl size="small" sx={{ minWidth: 170 }}>
                      <Select
                        value={candidateStatusFilter}
                        onChange={(event) => {
                          setCandidateStatusFilter(event.target.value);
                          setPaginationData((prev) => ({ ...prev, pageNumber: 1 }));
                        }}
                      >
                        <MenuItem value="All">All Application Stages</MenuItem>
                        <MenuItem value="Applied">Applied</MenuItem>
                        <MenuItem value="Ranked">Ranked</MenuItem>
                        <MenuItem value="Shortlisted">Shortlisted</MenuItem>
                        <MenuItem value="Finalized">Finalized</MenuItem>
                        <MenuItem value="Rejected">Rejected</MenuItem>
                        <MenuItem value="Ranking Failed">Ranking Failed</MenuItem>
                      </Select>
                    </FormControl> */}

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={paginationData.pageSize}
                        onChange={(event) =>
                          setPaginationData((prev) => ({
                            ...prev,
                            pageSize: Number(event.target.value),
                            pageNumber: 1,
                          }))
                        }
                      >
                        {PAGE_SIZE_OPTIONS.map((size) => (
                          <MenuItem key={size} value={size}>
                            {size} rows
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Button variant="outlined" onClick={resetCandidateFilters}>
                      Reset
                    </Button>
                  </Stack>
                </Stack>
              </Paper>

              {error.candidates && (
                <Alert severity="error">{error.candidates}</Alert>
              )}
              {error.cv && <Alert severity="error">{error.cv}</Alert>}

              {loading.candidates ? (
                <Stack spacing={1}>
                  {[1, 2, 3, 4, 5].map((item) => (
                    <Skeleton
                      key={item}
                      variant="rectangular"
                      height={64}
                      sx={{ borderRadius: 2 }}
                    />
                  ))}
                </Stack>
              ) : candidates.length === 0 ? (
                <Box textAlign="center" py={8}>
                  <Person
                    sx={{ fontSize: 72, color: "text.disabled", mb: 1 }}
                  />
                  <Typography variant="h6" fontWeight={900}>
                    No applications yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Candidate applications for this job will appear here.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    {renderTableHeader()}

                    <TableBody>
                      {sortedCandidates.map((candidate, index) =>
                        renderCandidateRow(candidate, index),
                      )}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {candidates.length > 0 && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  sx={{ pt: 1 }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ArrowBack />}
                    disabled={!paginationData.hasPreviousPage}
                    onClick={() =>
                      setPaginationData((prev) => ({
                        ...prev,
                        pageNumber: Math.max(prev.pageNumber - 1, 1),
                      }))
                    }
                  >
                    Previous
                  </Button>

                  <Typography variant="body2" color="text.secondary">
                    Page {paginationData.pageNumber} of{" "}
                    {paginationData.totalPages || 1} ·{" "}
                    {paginationData.totalCount} total candidates
                  </Typography>

                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<ArrowForward />}
                    disabled={!paginationData.hasNextPage}
                    onClick={() =>
                      setPaginationData((prev) => ({
                        ...prev,
                        pageNumber: prev.pageNumber + 1,
                      }))
                    }
                  >
                    Next
                  </Button>
                </Stack>
              )}
            </Stack>
          </Paper>
        )}

        {selectedCandidates.length > 0 && (
          <Paper
            elevation={8}
            sx={{
              position: "fixed",
              bottom: 24,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1300,
              px: 2,
              py: 1.2,
              borderRadius: 99,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              bgcolor: "grey.900",
              color: "white",
            }}
          >
            <Typography variant="body2" fontWeight={900}>
              {selectedCandidates.length} selected
            </Typography>

            <Button
              size="small"
              variant="contained"
              startIcon={<Download />}
              onClick={handleBulkDownload}
              disabled={loading.cv}
            >
              Download ZIP
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={() => setSelectedCandidates([])}
              sx={{ color: "white", borderColor: "rgba(255,255,255,0.45)" }}
            >
              Clear
            </Button>
          </Paper>
        )}

        <Dialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box>
              <Typography fontWeight={900}>{previewCandidateName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {sortedCandidates[previewCandidateIndex]?.email || ""}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                disabled={previewCandidateIndex <= 0}
                onClick={() => goToPreviewCandidate("previous")}
              >
                Previous
              </Button>

              <Typography variant="body2">
                {sortedCandidates.length ? previewCandidateIndex + 1 : 0} /{" "}
                {sortedCandidates.length}
              </Typography>

              <Button
                size="small"
                variant="outlined"
                disabled={previewCandidateIndex >= sortedCandidates.length - 1}
                onClick={() => goToPreviewCandidate("next")}
              >
                Next
              </Button>

              <IconButton size="small" onClick={() => setPreviewOpen(false)}>
                <Close fontSize="small" />
              </IconButton>
            </Stack>
          </DialogTitle>

          <DialogContent sx={{ p: 0, height: "85vh", bgcolor: "grey.100" }}>
            <Box sx={{ position: "relative", height: "100%" }}>
              {previewLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(255,255,255,0.7)",
                  }}
                >
                  <CircularProgress />
                </Box>
              )}

              {previewUrl ? (
                <iframe
                  src={previewUrl}
                  title="Candidate CV Preview"
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                />
              ) : (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography color="text.secondary">
                    No CV preview available.
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      </Stack>
    </Box>
  );
};

export default CandidateTrackingPage;